import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { AccountabilityChart } from "@/components/diagrams/AccountabilityChart";
import { ArrowRight, Users } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import leadershipTableImg from "@/assets/photos/leadership-table.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const PATH = "/specialisations/roles-targets-and-incentives";
const TITLE =
  "Role design, targets and incentive structures";
const DESCRIPTION =
  "Accountability charts, role requirements, measurable targets per seat, and bonus and commission structures modelled against the P&L before announcement.";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure:
      "A full review of the accountability structure, seat by seat, alongside ~$1M p.a. of non-critical people overhead removed in the first three months",
    org: "Mining services",
  },
  {
    figure: "A bonus policy change delivering $200K in FY16 savings",
    org: "A 249-store national retailer",
  },
  {
    figure:
      "A company-wide bonus scheme where everyone, not just the top of the house, had a stake in the result. It was one of four levers behind four consecutive years of profit growth",
    org: "A transport group",
  },
  {
    figure:
      "Financial modelling for enterprise bargaining negotiations delivering a $1.2M three-year cost benefit",
    org: "The same retailer",
  },
  {
    figure: "A two-role restructure delivering $250K",
    org: "An electrical contracting business",
  },
];

const covers = [
  {
    title: "Accountability charts",
    desc: "One name against every function, so no seat is shared by two people and no function is owned by nobody.",
  },
  {
    title: "Role requirements and position descriptions",
    desc: "What the seat is responsible for, what it must be able to do, and what good looks like in it, written before anyone is considered for it.",
  },
  {
    title: "Measurable targets and quarterly objectives",
    desc: "A small number of numbers per seat, reviewed each quarter, that the person holding the seat can actually influence.",
  },
  {
    title: "Bonus and commission design",
    desc: "Incentive structures modelled against the P&L at several volume and margin outcomes before they are announced, so the cost of success is known in advance.",
  },
];

const faqs = specialisationFaqs["roles-targets-and-incentives"];

export const Route = createFileRoute("/specialisations/roles-targets-and-incentives")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-roles-targets-and-incentives", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Roles, targets and incentives", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Role design, targets and incentive structures",
          description: DESCRIPTION,
          path: PATH,
          offerId: "remuneration-incentive-design",
          slug: "roles-targets-and-incentives",
          serviceType: "Organisational design",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: RolesSpecialisation,
});

function RolesSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <Users className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">
            Expertise: roles, targets and incentives
          </span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            Most businesses hire before they have decided what the job is.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            A role design consultant defines what each seat owns, its measurable requirements, its targets and its decision rights, aligned to the business the next 12 to 36 months need. Ajility does this work and leaves recruitment to recruiters. 
            <Link to="/insights/$slug" params={{ slug: "right-people-right-seats" }} className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">This work is about structure, not recruitment</Link>. Ajility designs the role, the
            requirements, the targets and the accountabilities. It does not run the search.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_roles_targets_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See the priced modules
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={leadershipTableImg}
        alt="A small leadership team working through notes around a plain table in a modest office"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Decide the job before you fill it
          </h2>
          <div className="mt-12">
            <AccountabilityChart />
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm leading-relaxed text-charcoal">
            Where the line sits. Ajility designs the commercial architecture: what the business is
            buying, the measure, the shape of the payout curve, the cost of the scheme at every
            outcome, and the rules in plain commercial language. Ajility does not give employment
            law advice. Award coverage, whether a person is an employee or a contractor, whether a
            deduction or a clawback is lawful, and whether a scheme rule is an unfair contract term
            are questions for your employment lawyer, and they are put to them in writing before the
            scheme is announced. From 1 July 2026 superannuation is payable on all commissions under
            the qualifying earnings rules, so the on-cost sits in the model rather than being
            discovered afterwards.
          </p>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Structure and incentive work delivered in senior operating roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map((p) => (
              <li key={p.figure} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Buy it on its own</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Remuneration and Incentive Design, People, Culture &amp; Accountability install, Org
            Design Sprint
          </h2>
          <p className="mt-4 text-charcoal">
            This area of expertise sits inside every Fractional COO engagement, and each module can
            also be bought on its own. Scope and fees are published on the packages page.
          </p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "commission-and-bonus-structure-design" }} className="underline underline-offset-4">commission and bonus structure design</Link> for the practical questions behind this work.</p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See the modules
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="roles-targets-and-incentives" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_roles_targets_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
